import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Edit2, Trash2, Eye, ChevronLeft, ChevronRight, ArrowLeft, Home, Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { getLoginUrl } from "@/const";
import { useState } from "react";

export default function AdminApartmentModeration() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, loading } = useAuth();
  const [currentPage, setCurrentPage] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteReason, setDeleteReason] = useState("");

  const isAdmin = isAuthenticated && user?.role === "admin";
  const limit = 10;
  const { data: result, isLoading, refetch } = trpc.apartments.adminListApproved.useQuery(
    { limit, offset: currentPage * limit },
    { enabled: isAdmin }
  );

  const editMutation = trpc.apartments.adminEditListing.useMutation();
  const deleteMutation = trpc.apartments.adminDeleteListing.useMutation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-md mx-auto p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-6">You need admin privileges to access this page.</p>
            <Button onClick={() => (window.location.href = getLoginUrl("/admin/apartments-moderation"))} className="w-full bg-blue-600 hover:bg-blue-700">
              Login as Admin
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const handleEdit = (listing: any) => {
    setEditingId(listing.id);
    setEditData({
      flatNumber: listing.flatNumber,
      price: listing.price,
      areaInSqFt: listing.areaInSqFt,
      floor: listing.floor,
      facing: listing.facing,
      amenities: listing.amenities || [],
      description: listing.description,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editData) return;

    try {
      await editMutation.mutateAsync({
        id: editingId,
        ...editData,
      });
      toast.success("Listing updated successfully");
      setEditingId(null);
      setEditData(null);
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update listing");
    }
  };

  const handleDelete = async () => {
    if (!deleteId || !deleteReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        id: deleteId,
        reason: deleteReason,
      });
      toast.success("Listing deleted successfully and resident notified");
      setDeleteId(null);
      setDeleteReason("");
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete listing");
    }
  };

  const listings = result?.listings || [];
  // If the page returned a full page of results there may be more; otherwise we're on the last page
  const hasNextPage = listings.length === limit;
  const hasPrevPage = currentPage > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <Home className="w-4 h-4" />
            Home
          </Button>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Apartment Listings - Post-Moderation</h1>
          <p className="text-lg text-slate-600">Review, edit, or delete apartment listings that are currently live</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-slate-600">Loading listings...</p>
          </div>
        ) : listings.length === 0 ? (
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Listings to Moderate</h3>
            <p className="text-slate-600">All apartment listings have been reviewed.</p>
          </Card>
        ) : (
          <>
            <div className="grid gap-6 mb-8">
              {listings.map((listing: any) => (
                <Card key={listing.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">
                        {listing.bhk} BHK {listing.apartmentType} - Flat {listing.flatNumber}
                      </h3>
                      <p className="text-slate-600 mt-1">
                        {listing.listingType === "rent" ? "For Rent" : "For Sale"} • ₹{listing.price?.toLocaleString()}
                        {listing.listingType === "rent" ? "/month" : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Posted by</p>
                      <p className="font-semibold text-slate-900">{listing.residentName}</p>
                      <p className="text-sm text-slate-500">{listing.residentEmail}</p>
                    </div>
                  </div>

                  <div className="flex gap-3 mb-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(listing)}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Edit Listing - Flat {listing.flatNumber}</DialogTitle>
                        </DialogHeader>
                        {editingId === listing.id && editData && (
                          <div className="space-y-4 max-h-96 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Flat Number</label>
                                <Input
                                  value={editData.flatNumber}
                                  onChange={(e) => setEditData({ ...editData, flatNumber: e.target.value })}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Price</label>
                                <Input
                                  type="number"
                                  value={editData.price}
                                  onChange={(e) => setEditData({ ...editData, price: Number(e.target.value) })}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Area (sq ft)</label>
                                <Input
                                  type="number"
                                  value={editData.areaInSqFt}
                                  onChange={(e) => setEditData({ ...editData, areaInSqFt: Number(e.target.value) })}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Floor</label>
                                <Input
                                  type="number"
                                  value={editData.floor || ""}
                                  onChange={(e) => setEditData({ ...editData, floor: Number(e.target.value) || undefined })}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Facing</label>
                                <Input
                                  value={editData.facing || ""}
                                  onChange={(e) => setEditData({ ...editData, facing: e.target.value })}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                              <Textarea
                                value={editData.description}
                                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                rows={4}
                              />
                            </div>
                            <div className="flex gap-3 pt-4">
                              <Button onClick={handleSaveEdit} className="flex-1 bg-blue-600 hover:bg-blue-700">
                                Save Changes
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setEditingId(null);
                                  setEditData(null);
                                }}
                                className="flex-1"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="destructive" size="sm" onClick={() => setDeleteId(listing.id)}>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Delete Listing - Flat {listing.flatNumber}</DialogTitle>
                        </DialogHeader>
                        {deleteId === listing.id && (
                          <div className="space-y-4">
                            <p className="text-slate-600">
                              Are you sure you want to delete this listing? The resident will be notified with the reason.
                            </p>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Reason for Deletion</label>
                              <Textarea
                                placeholder="E.g., Violates community guidelines, Duplicate listing, etc."
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                rows={4}
                              />
                            </div>
                            <div className="flex gap-3">
                              <Button
                                onClick={handleDelete}
                                className="flex-1 bg-red-600 hover:bg-red-700"
                                disabled={!deleteReason.trim()}
                              >
                                Delete & Notify Resident
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setDeleteId(null);
                                  setDeleteReason("");
                                }}
                                className="flex-1"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="text-sm text-slate-500">
                    Posted on {new Date(listing.createdAt).toLocaleDateString()}
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {(hasPrevPage || hasNextPage) && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={!hasPrevPage}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <span className="text-slate-600">Page {currentPage + 1}</span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasNextPage}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
